import { InterpolationMethod, SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { getStatisticFragment } from '@/utilities/statistic.utilities';
import type { BackendDuration, FrontendDuration } from '@/services/systemConfiguration.types';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { ToolConditionMetadata, ToolItem } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export type SignalFromConditionItem = Omit<ToolItem, 'conditionMetadata'> & {
  conditionMetadata?: ToolConditionMetadata & {
    maxInterpolation?: BackendDuration;
  };
};

export type SignalFromConditionFormulaParams = {
  interpolation: InterpolationMethod;
  stat: StoredStatistic;
  maximumDuration: {
    inputOverride?: ValueWithUnitsItem;
    boundingOverride?: ValueWithUnitsItem;
  };
  keyMethod: string;
};

export type SignalFromConditionSignalMetadata = {
  maxInterpolation?: FrontendDuration;
};

export type SignalFromConditionFormulaState = {
  signalMetadata: SignalFromConditionSignalMetadata | undefined;
  interpolation: InterpolationMethod;
  keyMethod: string | undefined;
  maximumDurationBoundingOverride: ValueWithUnitsItem | undefined;
  maximumDurationInputOverride: ValueWithUnitsItem | undefined;
  stat: StoredStatistic;
};

type SignalFromConditionFormulaOptions = {
  condition: SignalFromConditionItem;
  inputItem: SignalFromConditionItem;
  maxInterpolation?: FrontendDuration | null;
  params: SignalFromConditionFormulaParams;
};

type ParseSignalFromConditionFormulaOptions = {
  defaultMaximumDuration?: FrontendDuration;
  formula?: string;
  signalMaxInterpolation?: BackendDuration;
};

export function getSignalFromConditionFormula({
  condition,
  inputItem,
  maxInterpolation,
  params,
}: SignalFromConditionFormulaOptions): string {
  const keyMethod = SAMPLE_FROM_SCALARS.KEY_METHODS.find((method) => method.key === params.keyMethod);
  if (!keyMethod) {
    throw new Error(`Unknown Signal from Condition timestamp method: ${params.keyMethod}`);
  }

  let maxGapSnippet = maxInterpolation ? `, ${maxInterpolation.value}${maxInterpolation.units}` : '';
  if (params.interpolation === InterpolationMethod.DISCRETE) {
    maxGapSnippet = ', 0s';
  }

  const step = params.interpolation === InterpolationMethod.STEP ? '.toStep()' : '';
  const stat = getStatisticFragment(params.stat);

  let series = '$series';
  if (inputItem.itemType === ITEM_TYPES.CONDITION && inputItem.conditionMetadata?.maximumDuration === undefined) {
    series += getMaximumDurationFragment(params.maximumDuration.inputOverride, 'input');
  }

  let capsules = '$capsules';
  if (condition.conditionMetadata?.maximumDuration === undefined) {
    capsules += getMaximumDurationFragment(params.maximumDuration.boundingOverride, 'bounding');
  }

  return `${series}.aggregate(${stat}, ${capsules}, ${keyMethod.stat}${maxGapSnippet})${step}`;
}

export function getSignalFromConditionParameters(
  conditionId: string,
  signalId: string,
): { capsules: string; series: string } {
  return { capsules: conditionId, series: signalId };
}

export function parseSignalFromConditionFormula({
  defaultMaximumDuration,
  formula = '',
  signalMaxInterpolation,
}: ParseSignalFromConditionFormulaOptions): SignalFromConditionFormulaState {
  const inputOverrideDuration = /\$series\.setMaximumDuration\((.*?)\)/.exec(formula)?.[1];
  const boundingOverrideDuration = /\$capsules\.setMaximumDuration\((.*?)\)/.exec(formula)?.[1];
  const { stat, parameters } = getStatAndParameters(formula);
  const keyMethod = /(\w*?)Key\(\)/.exec(formula)?.[1];

  const interpolation = formula.includes('toStep()')
    ? InterpolationMethod.STEP
    : Number(signalMaxInterpolation?.value ?? 0) > 0
      ? InterpolationMethod.LINEAR
      : InterpolationMethod.DISCRETE;

  return {
    signalMetadata: signalMaxInterpolation
      ? { maxInterpolation: { value: signalMaxInterpolation.value, units: signalMaxInterpolation.uom } }
      : undefined,
    interpolation,
    keyMethod,
    maximumDurationBoundingOverride:
      (boundingOverrideDuration ? parseDuration(boundingOverrideDuration) : undefined) ?? defaultMaximumDuration,
    maximumDurationInputOverride:
      (inputOverrideDuration ? parseDuration(inputOverrideDuration) : undefined) ?? defaultMaximumDuration,
    stat: {
      key: stat?.key ?? null,
      timeUnits: stat?.timeUnits ?? parameters.timeUnits,
      percentile: stat?.percentile ?? parameters.percentile,
      multiplier: stat?.multiplier ?? parameters.multiplier,
    },
  };
}

function parseDuration(duration: string): ValueWithUnitsItem | undefined {
  const value = duration.match(/[-+]?[0-9]*\.?[0-9]+/)?.[0];
  const units = duration.match(/[a-zA-Z]+/)?.[0];
  return value && units ? { value: Number(value), units } : undefined;
}

function getMaximumDurationFragment(override: ValueWithUnitsItem | undefined, name: 'bounding' | 'input'): string {
  if (override) {
    return `.setMaximumDuration(${override.value}${override.units})`;
  }

  throw new Error(`Signal from Condition ${name} Condition must have a value for maximumDuration`);
}

function getStatAndParameters(formula: string): {
  parameters: { multiplier: number | null; percentile: number | null; timeUnits: string };
  stat: StoredStatistic | null;
} {
  const match = /\.aggregate\((\w*)\((?:'|")?(.*?)(?:'|")?\)/.exec(formula);
  if (!match) {
    return { stat: null, parameters: { multiplier: null, percentile: null, timeUnits: 's' } };
  }

  const [, operator, arg] = match;
  const method = SAMPLE_FROM_SCALARS.VALUE_METHODS.find((valueMethod) => valueMethod.stat.startsWith(operator));
  if (!method) {
    return { stat: null, parameters: { multiplier: null, percentile: null, timeUnits: 's' } };
  }

  const parameters = { multiplier: null as number | null, percentile: null as number | null, timeUnits: 's' };
  const storedStat: StoredStatistic = {
    key: method.key,
    timeUnits: 's',
    percentile: null,
    multiplier: null,
  };

  if (method.key === 'percentile') {
    const percentile = Number.parseInt(arg, 10);
    const parsedPercentile = Number.isNaN(percentile) ? null : percentile;
    parameters.percentile = parsedPercentile;
    storedStat.percentile = parsedPercentile;
  } else if (method.key === 'standardDeviationBoundary') {
    const multiplier = Number.parseFloat(arg);
    const parsedMultiplier = Number.isNaN(multiplier) ? null : multiplier;
    parameters.multiplier = parsedMultiplier;
    storedStat.multiplier = parsedMultiplier;
  } else {
    const timeUnits = arg && !['true', 'false'].includes(arg.toLowerCase()) ? arg : 's';
    parameters.timeUnits = timeUnits;
    storedStat.timeUnits = timeUnits;
  }

  return { stat: storedStat, parameters };
}
